import { BiMinus, BiPlus } from "react-icons/bi";
import { Button } from "components/common/buttons/Button";
import { cn } from "utils/cn";

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const segmentButtonClass = (active: boolean) =>
  cn(
    "flex-1 rounded-md px-3 py-2 text-xs font-medium transition-colors",
    active ? "bg-muted text-primary" : "text-muted-foreground hover:text-muted-foreground"
  );

interface NumberStepperProps {
  value: number;
  onChange: (next: number) => void;
  min: number;
  max: number;
  /** Unit rendered inside the field (e.g. "hrs"). Omit when the unit label lives outside the control. */
  unit?: string;
  ariaLabel: string;
  /** Inline use (e.g. "Every 5 hrs") — keeps the control compact instead of stretching. */
  compact?: boolean;
  size?: "sm" | "md";
  /** Renders +/- inside a single bordered field with dividers. */
  layout?: "default" | "inset";
}

export const NumberStepper = ({
  value,
  onChange,
  min,
  max,
  unit,
  ariaLabel,
  compact = false,
  size = "md",
  layout = "default",
}: NumberStepperProps) => {
  const commit = (next: number) => onChange(clamp(next, min, max));
  const isSmall = size === "sm";
  const iconSize = isSmall ? 14 : 16;

  const sideButtonClass = cn(
    "shrink-0 self-stretch p-0 text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-25",
    isSmall ? "[&_svg]:h-3.5 [&_svg]:w-3.5" : "",
    layout === "inset"
      ? cn("h-auto rounded-none", isSmall ? "w-7" : "w-9")
      : cn("rounded-md", isSmall ? "h-7 w-7" : "h-9 w-9 rounded-lg")
  );

  const valueBlock = (
    <div
      className={cn(
        "flex items-center justify-center",
        layout === "inset"
          ? "min-w-0 flex-1 gap-1 self-stretch border-x border-border"
          : compact
            ? isSmall
              ? "min-w-[4.25rem] gap-1"
              : "min-w-[5.5rem] gap-1.5"
            : "min-w-0 flex-1 gap-2"
      )}
    >
      <input
        type="number"
        inputMode="numeric"
        aria-label={ariaLabel}
        min={min}
        max={max}
        value={value}
        onKeyDown={(event) => {
          if (event.code === "Minus") event.preventDefault();
        }}
        onChange={(event) => {
          const raw = event.target.value;
          if (raw === "") return;
          commit(Number(raw));
        }}
        className={cn(
          "bg-transparent text-center font-semibold tabular-nums text-foreground focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
          isSmall ? "w-8 text-sm" : compact ? "w-12 text-lg" : "w-12 text-xl"
        )}
      />
      {unit ? (
        <span className={cn("shrink-0 text-muted-foreground/60", isSmall ? "text-xs" : "text-sm")}>
          {unit}
        </span>
      ) : null}
    </div>
  );

  if (layout === "inset") {
    return (
      <div
        className={cn(
          "flex w-full overflow-hidden rounded-md border border-border bg-muted/40",
          isSmall ? "h-8" : "h-10"
        )}
      >
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Decrease ${ariaLabel}`}
          disabled={value <= min}
          className={sideButtonClass}
          onClick={() => commit(value - 1)}
        >
          <BiMinus size={iconSize} />
        </Button>
        {valueBlock}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Increase ${ariaLabel}`}
          disabled={value >= max}
          className={sideButtonClass}
          onClick={() => commit(value + 1)}
        >
          <BiPlus size={iconSize} />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center",
        compact && "w-full justify-center",
        isSmall ? "gap-1" : compact ? "gap-2" : "gap-1"
      )}
    >
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Decrease ${ariaLabel}`}
        disabled={value <= min}
        className={sideButtonClass}
        onClick={() => commit(value - 1)}
      >
        <BiMinus size={iconSize} />
      </Button>
      {valueBlock}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Increase ${ariaLabel}`}
        disabled={value >= max}
        className={sideButtonClass}
        onClick={() => commit(value + 1)}
      >
        <BiPlus size={iconSize} />
      </Button>
    </div>
  );
};
